import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useSubscriptionStatus from '../../../hooks/useSubscriptionStatus';
import UsageProgressBar from '../../common/UsageProgressBar';
import CompanyTokensInfoIcon from './CompanyTokensInfoIcon';
import FallbackActivationModal from './FallbackActivationModal';
import OnHoldActivationModal from './OnHoldActivationModal';
import useAgentUsageRefetch from './useAgentUsageRefetch';
import { SUPPORT_EMAIL } from '../../../utils/constants/subscription';

const getCompactTitle = ({ onHold, inFallback, percentRemaining }) => {
  if (onHold) return 'Company Tokens — On hold';
  if (inFallback) return `Company Tokens — ${percentRemaining}% remaining (fallback mode)`;
  return `Company Tokens — ${percentRemaining}% remaining`;
};

const AgentTokenDisplay = ({
  collapsed = false,
  tokens: tokensProp,
  subscribed: subscribedProp,
  isLoading: isLoadingProp,
  ackNotification: ackNotificationProp
}) => {
  // Controlled mode (TD-2883): AgentUsagePanel owns one /subscription-status
  // instance for the whole usage block and passes the token figures down, so
  // adding meters doesn't add requests (see TD-2704). `tokens === undefined`
  // (not `null`) is the switch — a parent that has access but is still loading
  // passes `null`, which must stay controlled so the internal fetch stays off.
  const isControlled = tokensProp !== undefined;
  const hook = useSubscriptionStatus({ enabled: !isControlled });
  const tokens = isControlled ? tokensProp : hook.tokens;
  const subscribed = isControlled ? !!subscribedProp : hook.subscribed;
  const isLoading = isControlled ? !!isLoadingProp : hook.isLoading;
  const ackNotification = isControlled ? ackNotificationProp : hook.ackNotification;
  // TD-2443: one-shot guards so the ack POST fires once per period, not on every
  // status poll between opening the modal and the BE reflecting the ack. Reset
  // when the BE clears the notify flag (next period), which re-arms the popup.
  const fallbackAckedRef = useRef(false);
  const onholdAckedRef = useRef(false);
  const [fallbackModalOpen, setFallbackModalOpen] = useState(false);
  const [onHoldModalOpen, setOnHoldModalOpen] = useState(false);

  // TD-2002: refresh the token counter when an agent response completes.
  // Skipped in controlled mode — the panel above owns the refetch.
  useAgentUsageRefetch(hook.refetch, !isControlled);

  // TD-2443: The BE owns "has this user already been notified for the current
  // token period" via fallback_notify / onhold_notify on /subscription-status,
  // replacing the old per-browser-session sessionStorage gate that re-showed the
  // popup on every new session. When a notify flag is set we open the modal once
  // and POST an ack so the BE suppresses it until the period resets. On-hold
  // precedence (no fallback popup while the pool is exhausted) is enforced
  // server-side — fallback_notify is false whenever on hold.
  const fallbackNotify = tokens?.fallbackNotify ?? false;
  const onholdNotify = tokens?.onholdNotify ?? false;

  useEffect(() => {
    if (!fallbackNotify) {
      // Period reset (or not in fallback) — re-arm for the next period.
      fallbackAckedRef.current = false;
      return;
    }
    if (fallbackAckedRef.current) return;
    fallbackAckedRef.current = true;
    setFallbackModalOpen(true);
    ackNotification('fallback');
  }, [fallbackNotify, ackNotification]);

  useEffect(() => {
    if (!onholdNotify) {
      onholdAckedRef.current = false;
      return;
    }
    if (onholdAckedRef.current) return;
    onholdAckedRef.current = true;
    // On-hold supersedes fallback: close the fallback popup if it is still open.
    setFallbackModalOpen(false);
    setOnHoldModalOpen(true);
    ackNotification('onhold');
  }, [onholdNotify, ackNotification]);

  // Don't render while loading with no data yet, when not subscribed, or when no
  // token limit. In controlled mode, isLoading may flip on refetch while tokens
  // are still present — keep the row mounted like the panel does above.
  // limit === 0 means unlimited (hook normalises null → 0)
  if ((isLoading && tokens == null) || !subscribed || !tokens?.limit) return null;

  const onHold = tokens.limitReached;
  const inFallback = !onHold && tokens.fallbackActive;
  const percentRemaining = Math.max(0, 100 - tokens.percentage);

  const compactTitle = getCompactTitle({ onHold, inFallback, percentRemaining });

  const view = collapsed ? (
    <div className="agent-token-display agent-token-display-compact" title={compactTitle}>
      <div className={`agent-token-dot agent-token-dot-${onHold ? 'grey' : tokens.color}`} />
    </div>
  ) : (
    <div
      className={`agent-token-display${
        onHold ? ' agent-token-display-on-hold' : ''
      }${inFallback ? ' agent-token-display-fallback' : ''}`}
    >
      <div className="company-tokens-header">
        <span className={`company-tokens-label${onHold ? ' company-tokens-label-onhold' : ''}`}>
          Company Tokens
        </span>
        <CompanyTokensInfoIcon fallbackActive={inFallback} onHold={onHold} />
        {/*
          The figure sits on the label row rather than under the bar: three
          balances stacked in the rail cost a line each, and the sidebar has
          little vertical room to give. Fallback mode has no subline of its own
          either — the (i) tooltip already explains it and when it resets.
        */}
        <span className="agent-usage-meter-value">{percentRemaining}%</span>
      </div>
      <UsageProgressBar
        used={tokens.used}
        limit={tokens.limit}
        displayMode="bare"
        forceGrey={onHold}
      />
      {onHold && (
        <span className="onhold-subline">
          On hold ·{' '}
          <a
            className="onhold-subline-link"
            href={`mailto:${SUPPORT_EMAIL}`}
            aria-label={`Email sales at ${SUPPORT_EMAIL}`}
          >
            contact sales
          </a>
        </span>
      )}
    </div>
  );

  // Modals render alongside the view in BOTH collapsed and expanded modes —
  // a one-time R2d/R3 popup is independent of sidebar layout state.
  return (
    <>
      {view}
      <FallbackActivationModal
        isOpen={fallbackModalOpen}
        onClose={() => setFallbackModalOpen(false)}
      />
      <OnHoldActivationModal isOpen={onHoldModalOpen} onClose={() => setOnHoldModalOpen(false)} />
    </>
  );
};

AgentTokenDisplay.propTypes = {
  collapsed: PropTypes.bool,
  // Controlled mode (TD-2883): when `tokens` is provided the component renders
  // from these and does not fetch. Shape mirrors useSubscriptionStatus().tokens.
  tokens: PropTypes.shape({
    used: PropTypes.number,
    limit: PropTypes.number,
    color: PropTypes.string,
    percentage: PropTypes.number,
    limitReached: PropTypes.bool,
    fallbackActive: PropTypes.bool,
    fallbackNotify: PropTypes.bool,
    onholdNotify: PropTypes.bool
  }),
  subscribed: PropTypes.bool,
  isLoading: PropTypes.bool,
  ackNotification: PropTypes.func
};

export default AgentTokenDisplay;
